import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEnglishUi, languageNameFor, useUiLanguage } from '@/features/chat-trf/language';
import { apiClient } from '@/services/api';
import { useToast } from '@/components/ui/use-toast';
import { serverMessage } from '@/lib/serverMessage';

interface Props {
  originalText: string;
  originalLanguage: string;
  translatedText: string;
  targetLanguage: string;
  conversationId: string;
  messageId: string;
  onClose: () => void;
}

export function ViewOriginalModal({
  originalText,
  originalLanguage,
  translatedText,
  targetLanguage,
  conversationId,
  messageId,
  onClose,
}: Props) {
  const { t } = useTranslation(['chat', 'common']);
  const uiLanguage = useUiLanguage();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);

  const handleReport = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      // Use apiClient so the request goes through the auth interceptor
      // and includes the Bearer token. A bare `fetch` with only
      // `credentials: 'include'` silently 401s because the backend
      // auth middleware requires the Authorization header (B-048).
      await apiClient.post('/translation-reports', {
        conversation_id: conversationId,
        message_id: messageId,
        original_language: originalLanguage,
        target_language: targetLanguage,
        original_text: originalText,
        translated_text: translatedText,
      });
      toast({
        title: t('handoff.original.reported'),
        description: t('handoff.original.reportedBody'),
      });
      onClose();
    } catch (err) {
      // Surface the failure rather than closing silently. This is the
      // failure mode that hid B-048 for an entire implementation cycle —
      // don't repeat it.
      toast({
        title: t('handoff.original.reportFailed'),
        description: isEnglishUi(uiLanguage) ? serverMessage(err, t('handoff.original.reportFailedBody')) : t('handoff.original.reportFailedBody'),
        variant: 'destructive',
      });
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-stevens-scrim flex items-center justify-center z-50">
      <div className="bg-stevens-surface text-stevens-ink rounded-lg p-4 max-w-md w-full mx-4 shadow-xl">
        <h3 className="font-semibold mb-2">
          {isEnglishUi(uiLanguage)
            ? t('handoff.original.title', { language: originalLanguage.toUpperCase() })
            : t('handoff.original.title', { language: languageNameFor(originalLanguage, uiLanguage) })}
        </h3>
        <p className="text-body text-stevens-ink-3">{originalText}</p>
        <div className="mt-4 flex justify-between items-center">
          <button
            type="button"
            className="text-caption text-stevens-ink-3 underline disabled:opacity-50"
            onClick={handleReport}
            disabled={submitting}
          >
            {submitting ? t('handoff.original.submitting') : t('handoff.original.report')}
          </button>
          <button
            type="button"
            className="px-3 py-1 rounded bg-primary text-primary-foreground text-body"
            onClick={onClose}
          >
            {t('common:actions.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
